import React from 'react';

// Static "About this event" content keyed by event_slug.
//
// The copy itself avoids hardcoded numbers (e.g. "3 courts" / "8 weeks")
// so a single source of truth works across sessions of varying config.
// A dynamic "This session" summary row at the top surfaces the actual
// num_courts / players_per_court / weeks_planned / games_per_week values
// from the session row when present.

const MENS_NIGHT_ABOUT = {
  format: [
    "Men's Night is a weekly doubles event where each player earns individual points.",
    "Players are assigned to courts based on the session starting order set by the admin.",
    "Each court plays a rotating doubles format. Players earn points based on the games they win during that week's play.",
    "Sessions are played over multiple weeks, with standings tracked throughout the session.",
  ],
  movement: [
    "After each week is finalized, players move based on their point total on that court.",
    "The highest-scoring player on each court moves up one court.",
    "The lowest-scoring player on each court moves down one court.",
    "The two middle players stay on the same court.",
    "On Court 1, the highest-scoring player stays on Court 1.",
    "On the lowest court, the lowest-scoring player stays on that court.",
    "If there is a tie that affects movement, the tie is resolved before the week is finalized.",
  ],
  scoring: [
    "Standings are based on cumulative points across all finalized weeks.",
    "Weekly points are entered and finalized by the admin.",
    "If a player misses a week and no sub plays for them, they receive 0 points for that week.",
    "Absences are tracked in the session history.",
    "If a sub plays for a registered player, the points earned by the sub are credited to the registered player.",
  ],
  subs: [
    "Subs help keep courts full when a registered player cannot attend.",
    "A sub may play in place of a registered player for a specific week.",
    "Sub coordination may happen outside the app, but the admin can record when a sub played.",
    "Subs do not carry their own season standings for that session unless they are also registered as a regular player.",
  ],
  season: [
    "At the end of the session, the player with the most cumulative points is the session champion.",
    "Final standings remain available after the session is completed.",
    "Past sessions can be viewed as results/history.",
  ],
};

const LADIES_NIGHT_ABOUT = {
  ...MENS_NIGHT_ABOUT,
  format: [
    "Ladies Night is a weekly doubles event where each player earns individual points.",
    ...MENS_NIGHT_ABOUT.format.slice(1),
  ],
};

const ABOUT_BY_SLUG = {
  'mens-night': MENS_NIGHT_ABOUT,
  'ladies-night': LADIES_NIGHT_ABOUT,
};

function Section({ title, points }) {
  return (
    <section
      className="space-y-2"
      data-testid={`about-section-${title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
    >
      <h3 className="text-[11px] uppercase tracking-wider text-[#6a766e]">{title}</h3>
      <ul className="space-y-1.5 text-sm text-[#013523]">
        {points.map((p, i) => (
          <li key={i} className="flex gap-2">
            <span className="text-[#015e3f] mt-1">·</span>
            <span>{p}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

// Optional one-line summary of the actual session config — only renders
// the values that exist on the session row. Hidden entirely if no values.
function SessionConfigStrip({ session }) {
  if (!session) return null;
  const parts = [];
  if (session.num_courts && session.players_per_court) {
    parts.push(`${session.num_courts} courts × ${session.players_per_court} players`);
  }
  if (session.weeks_planned) {
    parts.push(`${session.weeks_planned} weeks`);
  }
  if (session.games_per_week) {
    parts.push(`${session.games_per_week} games per week`);
  }
  if (parts.length === 0) return null;
  return (
    <div
      data-testid="about-session-config"
      className="text-[11px] text-[#6a766e] bg-white border border-[#e3dcd1] rounded-lg px-3 py-2"
    >
      <span className="uppercase tracking-wider mr-2">This session</span>
      {parts.join(' · ')}
    </div>
  );
}

export default function EventAboutPanel({ session }) {
  const slug = session?.event_slug || 'mens-night';
  const about = ABOUT_BY_SLUG[slug];

  if (!about) {
    return (
      <div
        className="bg-[#fbfaf8] border border-[#e3dcd1] rounded-2xl p-4 text-sm text-[#6a766e]"
        data-testid="about-empty"
      >
        About content for this event type is coming soon.
      </div>
    );
  }

  return (
    <div
      className="bg-[#fbfaf8] border border-[#e3dcd1] rounded-2xl p-5 space-y-5"
      data-testid="event-about-panel"
    >
      <SessionConfigStrip session={session} />
      <Section title="Format" points={about.format} />
      <Section title="Court Movement" points={about.movement} />
      <Section title="Scoring & Attendance" points={about.scoring} />
      <Section title="Subs" points={about.subs} />
      <Section title="Season End" points={about.season} />
      <p className="text-[11px] text-[#6a766e] italic pt-2 border-t border-[#e3dcd1]">
        Rules and final decisions are subject to club admin discretion.
      </p>
    </div>
  );
}
